import { TRADING_SYMBOLS } from "@/data/symbolsTradingHours";
import type {
  LotInstrumentConfig,
  LotSizeCalculationInput,
  LotSizeResult,
} from "@/types/tradingTool";

export const DEPOSIT_CURRENCIES = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "AUD", label: "AUD" },
  { value: "CAD", label: "CAD" },
  { value: "JPY", label: "JPY" },
  { value: "CHF", label: "CHF" },
  { value: "NZD", label: "NZD" },
] as const;

const PIP_SIZE_BY_CATEGORY: Record<string, number> = {
  Forex: 0.00001,
  Exotics: 0.00001,
  Metals: 0.01,
  Cash: 0.00001,
  Crypto: 1,
  Equities: 0.01,
};

function parseContractSize(value: string): number {
  return Number(value.replace(/,/g, "")) || 0;
}

function parseLeverage(value: string): number {
  const match = value.match(/1:(\d+)/);
  return match ? Number(match[1]) : 100;
}

function getInstrumentBase(pair: string): string {
  return pair.split("-")[0]?.trim() ?? pair;
}

export const LOT_INSTRUMENTS: LotInstrumentConfig[] = TRADING_SYMBOLS.map(
  (symbol) => ({
    id: symbol.id,
    label: symbol.pair,
    base: getInstrumentBase(symbol.pair),
    contractSize: parseContractSize(symbol.contractSize),
    pipSize: PIP_SIZE_BY_CATEGORY[symbol.category] ?? 0.00001,
    leverage: parseLeverage(symbol.leverageSwing || symbol.leverage),
  }),
);

export function getStandardPipSize(pipSize: number): number {
  if (pipSize >= 0.01) return pipSize;
  if (pipSize >= 0.001) return pipSize * 10;
  return pipSize * 10;
}

export function calculateLotSize(input: LotSizeCalculationInput): LotSizeResult | null {
  const {
    accountBalance,
    contractSize,
    pipSize,
    stopLossPips,
    riskAmount,
    leverage,
  } = input;

  if (
    !Number.isFinite(accountBalance) ||
    accountBalance <= 0 ||
    !Number.isFinite(contractSize) ||
    contractSize <= 0 ||
    !Number.isFinite(pipSize) ||
    pipSize <= 0 ||
    !Number.isFinite(stopLossPips) ||
    stopLossPips <= 0 ||
    !Number.isFinite(riskAmount) ||
    riskAmount <= 0 ||
    !Number.isFinite(leverage) ||
    leverage <= 0
  ) {
    return null;
  }

  const standardPip = getStandardPipSize(pipSize);
  const pipValuePerLot = contractSize * standardPip;
  const lots = riskAmount / (stopLossPips * pipValuePerLot);
  const marginRequired = (lots * contractSize) / leverage;
  const freeMarginAfter = accountBalance - marginRequired;
  const riskPercent = (riskAmount / accountBalance) * 100;

  return {
    lots,
    marginRequired,
    freeMarginAfter,
    riskPercent,
    maxLoss: -riskAmount,
  };
}

export function formatMoney(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
